import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Clock, MapPin, Check, X, ArrowRight, Minus, Plus, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api } from "@/auth/AuthContext";
import { useCart, fmt } from "@/journeys/CartContext";
import { CurrencyToggle, CartLink } from "@/pages/JourneysShop";
import { useLang } from "@/contexts/LangContext";
import { jx, localizeJourney } from "@/i18n/journeyLabels";

const Counter = ({ label, sub, value, set, min = 0, max = 20, testId }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-[#EAE6DF] last:border-0">
    <div><p className="text-sm font-semibold">{label}</p><p className="text-[11px] text-[#4A4A4A]">{sub}</p></div>
    <div className="flex items-center gap-3">
      <button type="button" disabled={value <= min} onClick={() => set(value - 1)} data-testid={`${testId}-minus`}
        className="w-8 h-8 rounded-full border border-[#0E2A5C]/30 disabled:opacity-30 flex items-center justify-center"><Minus size={12} /></button>
      <span className="w-6 text-center font-medium" data-testid={`${testId}-count`}>{value}</span>
      <button type="button" disabled={value >= max} onClick={() => set(value + 1)} data-testid={`${testId}-plus`}
        className="w-8 h-8 rounded-full border border-[#0E2A5C]/30 disabled:opacity-30 flex items-center justify-center"><Plus size={12} /></button>
    </div>
  </div>
);

const EnquiryPanel = ({ j, defaultGroup }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", preferred_date: "", group_size: defaultGroup || 2, message: "" });
  const [st, setSt] = useState({ loading: false, done: false, error: null });
  const { lang } = useLang();
  const L = jx(lang);
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const inp = "w-full px-3 py-2.5 border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm";
  if (st.done) return <div className="mt-4 bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="enquiry-success">{L.enqSuccess}</div>;
  return (
    <form className="mt-5 border-t border-[#EAE6DF] pt-5 space-y-2.5" data-testid="enquiry-form" onSubmit={async (e) => {
      e.preventDefault();
      setSt({ loading: true, done: false, error: null });
      try {
        await api.post("/journeys/enquiry", { slug: j.slug, journey_title: j.title, ...f, group_size: Number(f.group_size) || null });
        setSt({ loading: false, done: true, error: null });
      } catch (er) { setSt({ loading: false, done: false, error: er?.response?.data?.detail || "Something went wrong — please try again." }); }
    }}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#C62828] font-semibold">✦ {L.enqTitle}</p>
      <input required placeholder={L.enqName} value={f.name} onChange={s("name")} data-testid="enquiry-name" className={inp} />
      <input required type="email" placeholder={L.enqEmail} value={f.email} onChange={s("email")} data-testid="enquiry-email" className={inp} />
      <input placeholder={L.enqPhone} value={f.phone} onChange={s("phone")} data-testid="enquiry-phone" className={inp} />
      <div className="grid grid-cols-2 gap-2.5">
        <input type="date" value={f.preferred_date} onChange={s("preferred_date")} data-testid="enquiry-date" className={inp} />
        <input type="number" min={1} placeholder={L.enqGroup} value={f.group_size} onChange={s("group_size")} data-testid="enquiry-group-size" className={inp} />
      </div>
      <textarea rows={3} placeholder={L.enqMessage} value={f.message} onChange={s("message")} data-testid="enquiry-message" className={inp} />
      {st.error && <p className="text-sm text-[#C62828]" data-testid="enquiry-error">{st.error}</p>}
      <button type="submit" disabled={st.loading} data-testid="enquiry-submit" className="w-full h-[48px] bg-[#0E2A5C] text-white text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-60">{st.loading ? L.enqSending : L.enqSend}</button>
    </form>
  );
};

export default function JourneyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, currency } = useCart();
  const [j, setJ] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [cfg, setCfg] = useState({ adults: 2, children: 0, infants: 0, room_type: "double", departure_id: null, preferred_date: "", addons: [] });
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState(null);
  const [showEnq, setShowEnq] = useState(false);
  const { lang } = useLang();
  const L = jx(lang);

  useEffect(() => {
    api.get(`/journeys/${slug}`).then((r) => {
      setJ(r.data.journey);
      setCfg((c) => ({
        ...c,
        adults: Math.max(c.adults, r.data.journey.min_travellers || 1),
        departure_id: r.data.journey.departures?.length ? r.data.journey.departures[0].id : c.departure_id,
      }));
    }).catch(() => setNotFound(true));
  }, [slug]);

  const fetchQuote = useCallback(() => {
    if (!j || j.price_on_request) { setQuote(null); return; }
    api.post(`/journeys/quote?currency=${currency}`, { slug, ...cfg })
      .then((r) => { setQuote(r.data.quote); setQuoteErr(null); })
      .catch((e) => { setQuote(null); setQuoteErr(e?.response?.data?.detail || "Unable to price this selection"); });
  }, [j, cfg, currency, slug]);
  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  if (notFound) return <div className="min-h-screen flex items-center justify-center">Journey not found — <Link className="ml-1 underline" to="/journeys">back to journeys</Link></div>;
  if (!j) return <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]"><Loader2 className="animate-spin text-[#C62828]" size={28} /></div>;

  const jl = localizeJourney(j, lang);
  const set = (k) => (v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggleAddon = (id) => setCfg((c) => ({ ...c, addons: c.addons.includes(id) ? c.addons.filter((x) => x !== id) : [...c.addons, id] }));
  const cartItem = () => ({ slug, title: j.title, hero_image: j.hero_image, duration_label: j.duration_label, ...cfg,
    departure_label: j.departures?.find((d) => d.id === cfg.departure_id)?.label || null });
  const addToCart = () => { addItem(cartItem()); navigate("/journeys/cart"); };

  return (
    <div className="min-h-screen bg-[#F7F5F0] font-body text-[#121212]">
      <SEO title={j.seo_title || j.title} description={j.seo_description || j.description} path={`/journeys/${slug}`} />
      <Header />
      <section className="relative h-[60vh] min-h-[420px]">
        <img src={j.hero_image} alt={j.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-[#0A0A0A]/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 max-w-7xl mx-auto px-6 md:px-12 pb-10 text-white">
          <span className="bg-[#0E2A5C]/90 text-[#D4AF37] text-[10px] font-bold tracking-[0.2em] px-3 py-1.5">{j.badge || j.category}</span>
          <h1 className="font-heading text-4xl md:text-6xl mt-4" data-testid="journey-title">{jl.title}</h1>
          {jl.subtitle && <p className="mt-2 text-lg text-[#D4AF37] uppercase tracking-[0.15em]">{jl.subtitle}</p>}
          <p className="mt-3 text-sm flex items-center gap-5 text-white/85">
            <span className="inline-flex items-center gap-1.5"><MapPin size={13} />{j.destination}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={13} />{j.duration_label}</span>
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-12">
          <div>
            <p className="text-lg text-[#4A4A4A] leading-relaxed">{jl.description}</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {(jl.highlights || []).map((h) => <span key={h} className="px-4 py-2 bg-white border border-[#EAE6DF] text-xs tracking-[0.1em] text-[#0E2A5C]">✦ {h}</span>)}
            </div>
          </div>
          <div data-testid="itinerary-timeline">
            <h2 className="font-heading text-3xl text-[#0E2A5C] mb-6">{L.itineraryH}</h2>
            <div className="border-l-2 border-[#D4AF37]/50 pl-6 space-y-7">
              {(jl.itinerary || []).map((d) => (
                <div key={d.day} className="relative">
                  <span className="absolute -left-[34px] top-1 h-4 w-4 rounded-full bg-[#D4AF37] border-4 border-[#F7F5F0]" />
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#C62828] font-bold">{L.dayLbl} {String(d.day).padStart(2, "0")}</p>
                  <h3 className="font-heading text-lg text-[#0E2A5C] mt-1">{d.title}</h3>
                  <p className="text-sm text-[#4A4A4A] mt-1 leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
          {j.gallery?.length > 0 && (
            <div data-testid="journey-gallery">
              <h2 className="font-heading text-3xl text-[#0E2A5C] mb-6">{L.galleryH}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {j.gallery.map((g, i) => (
                  <img key={`${g}-${i}`} src={g} alt={`${j.title} ${i + 1}`} loading="lazy" className="w-full h-44 object-cover border border-[#EAE6DF]" />
                ))}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white border border-[#EAE6DF] p-6">
              <h3 className="font-heading text-lg text-[#0E2A5C] mb-3">{L.includedH}</h3>
              {(jl.inclusions || []).map((x) => <p key={x} className="text-sm text-[#4A4A4A] py-1 flex gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" />{x}</p>)}
            </div>
            <div className="bg-white border border-[#EAE6DF] p-6">
              <h3 className="font-heading text-lg text-[#0E2A5C] mb-3">{L.notIncludedH}</h3>
              {(jl.exclusions || []).map((x) => <p key={x} className="text-sm text-[#4A4A4A] py-1 flex gap-2"><X size={14} className="text-[#C62828] mt-0.5 shrink-0" />{x}</p>)}
            </div>
          </div>
          <div className="bg-white border border-[#EAE6DF] p-6 text-sm text-[#4A4A4A] space-y-2.5">
            {jl.accommodation && <p><strong className="text-[#0E2A5C]">{L.accommodationH}:</strong> {jl.accommodation}</p>}
            {jl.transportation && <p><strong className="text-[#0E2A5C]">{L.transportH}:</strong> {jl.transportation}</p>}
            {jl.meals && <p><strong className="text-[#0E2A5C]">{L.mealsH}:</strong> {jl.meals}</p>}
            {jl.visa_info && <p><strong className="text-[#0E2A5C]">Visa:</strong> {jl.visa_info}</p>}
            {jl.notes && <p><strong className="text-[#0E2A5C]">{L.goodToKnow}:</strong> {jl.notes}</p>}
            {jl.cancellation_policy && <p><strong className="text-[#0E2A5C]">Terms:</strong> {jl.cancellation_policy}</p>}
          </div>
        </div>

        {/* Booking configurator */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-[#EAE6DF] p-6 md:p-8 lg:sticky lg:top-24" data-testid="booking-configurator">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl text-[#0E2A5C]">{L.configure}</h2>
              <CurrencyToggle />
            </div>
            {j.departures?.length > 0 ? (
              <div className="mb-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-2">{L.departure}</p>
                {j.departures.map((d) => (
                  <button key={d.id} type="button" onClick={() => set("departure_id")(d.id)} data-testid={`departure-${d.id}`}
                    disabled={d.availability === "sold_out"}
                    className={`w-full text-left px-4 py-3 mb-2 border text-sm transition-colors ${cfg.departure_id === d.id ? "border-[#C62828] bg-[#C62828]/5" : "border-[#EAE6DF] hover:border-[#0E2A5C]"} ${d.availability === "sold_out" ? "opacity-40" : ""}`}>
                    {d.label}
                    <span className={`ml-2 text-[9px] uppercase tracking-[0.15em] font-bold ${d.availability === "limited" ? "text-[#C62828]" : d.availability === "sold_out" ? "text-[#4A4A4A]" : "text-emerald-700"}`}>
                      {d.availability === "sold_out" ? "Sold out" : d.availability === "limited" ? "Limited availability" : "Available"}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mb-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-2">{L.travelDate}</p>
                <input type="date" value={cfg.preferred_date} onChange={(e) => set("preferred_date")(e.target.value)} data-testid="preferred-date"
                  className="w-full px-4 py-3 border border-[#EAE6DF] focus:border-[#0E2A5C] focus:outline-none text-sm" />
              </div>
            )}
            <Counter label={L.adults} sub={L.adultsHint} value={cfg.adults} set={set("adults")} min={1} testId="adults" />
            <Counter label={L.children} sub={L.childrenHint} value={cfg.children} set={set("children")} testId="children" />
            <Counter label={L.infants} sub={L.infantsHint} value={cfg.infants} set={set("infants")} max={cfg.adults} testId="infants" />
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mt-5 mb-2">{L.room}</p>
            <div className="grid grid-cols-4 gap-2">
              {(j.room_types || ["double", "single", "twin", "triple"]).map((r) => (
                <button key={r} type="button" onClick={() => set("room_type")(r)} data-testid={`room-${r}`}
                  className={`px-2 py-2 text-[11px] uppercase tracking-wider font-semibold border ${cfg.room_type === r ? "bg-[#0E2A5C] text-white border-[#0E2A5C]" : "border-[#EAE6DF] hover:border-[#0E2A5C]"}`}>{{ double: L.roomDouble, twin: L.roomTwin, single: L.roomSingle }[r] || r}</button>
              ))}
            </div>
            {j.addons?.length > 0 && (
              <div className="mt-5">
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-2">{L.addonsH}</p>
                {j.addons.map((a) => (
                  <label key={a.id} className="flex items-center gap-2.5 py-1.5 text-sm cursor-pointer">
                    <input type="checkbox" checked={cfg.addons.includes(a.id)} onChange={() => toggleAddon(a.id)} data-testid={`addon-${a.id}`} className="h-4 w-4 accent-[#C62828]" />
                    <span className="flex-1">{a.label}</span>
                    <span className="text-xs text-[#4A4A4A]">{fmt(currency === "AED" ? a.price_aed : a.price_usd, currency)}{a.per === "person" ? " /person" : ""}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="mt-6 pt-5 border-t border-[#EAE6DF]">
              {jl.pricing_note && <p className="mb-3 text-[11px] text-[#4A4A4A] leading-relaxed" data-testid="pricing-note">{jl.pricing_note}</p>}
              {j.price_on_request ? (
                <div data-testid="price-on-request">
                  <p className="font-heading text-2xl text-[#C62828]">{L.priceOnRequest}</p>
                  <button onClick={() => setShowEnq((v) => !v)} data-testid="request-journey" className="mt-4 w-full h-[50px] bg-[#0E2A5C] text-white text-xs uppercase tracking-[0.2em] font-semibold">{L.requestJourney}</button>
                </div>
              ) : quote ? (
                <div data-testid="dynamic-total">
                  {quote.price_band && <p className="mb-2 text-[11px] font-semibold text-[#0E2A5C]" data-testid="price-band">✦ {quote.price_band}</p>}
                  <div className="flex justify-between text-sm text-[#4A4A4A]"><span>{L.adults} × {cfg.adults}</span><span>{fmt(quote.breakdown.adults, currency)}</span></div>
                  {quote.breakdown.children > 0 && <div className="flex justify-between text-sm text-[#4A4A4A]"><span>{L.children} × {cfg.children}</span><span>{fmt(quote.breakdown.children, currency)}</span></div>}
                  {quote.breakdown.single_supplement > 0 && <div className="flex justify-between text-sm text-[#4A4A4A]"><span>Single supplement</span><span>{fmt(quote.breakdown.single_supplement, currency)}</span></div>}
                  {quote.breakdown.addons.map((a) => <div key={a.id} className="flex justify-between text-sm text-[#4A4A4A]"><span>{a.label}</span><span>{fmt(a.amount, currency)}</span></div>)}
                  <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-[#EAE6DF]">
                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C]">{L.total}</span>
                    <span className="font-heading text-3xl text-[#C62828]" data-testid="total-price">{fmt(quote.total, currency)}</span>
                  </div>
                  <button onClick={addToCart} data-testid="add-to-cart"
                    className="mt-5 w-full h-[52px] bg-[#C62828] hover:bg-[#B71C1C] text-white text-xs uppercase tracking-[0.2em] font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                    {L.addToCart} <ArrowRight size={14} />
                  </button>
                  <button onClick={() => { addItem(cartItem()); navigate("/journeys/checkout"); }} data-testid="book-now"
                    className="mt-2.5 w-full h-[52px] border border-[#0E2A5C] text-[#0E2A5C] hover:bg-[#0E2A5C] hover:text-white text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
                    {L.bookNow}
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-[#C62828]" data-testid="quote-error">{quoteErr || L.calculating}</p>
                  {quoteErr && (
                    <button onClick={() => setShowEnq((v) => !v)} data-testid="enquire-instead"
                      className="mt-3 w-full h-[46px] border border-[#0E2A5C] text-[#0E2A5C] hover:bg-[#0E2A5C] hover:text-white text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
                      {L.enquireInstead}
                    </button>
                  )}
                </div>
              )}
              {showEnq && <EnquiryPanel j={j} defaultGroup={cfg.adults + cfg.children} />}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
